import { z } from "zod";
import { SocialPlatformSchema } from "../enums/social";
import { SandboxTuningConfigSchema } from "./sandbox";

/**
 * One Apify Actor pinned by name AND immutable build ID. The worker asserts the
 * resolved build matches `buildId` before it spends anything, so an Actor
 * republished under the same version tag fails loudly instead of silently
 * changing our output shape.
 */
export const ActorPinSchema = z.strictObject({
  actorId: z.string().min(1),
  build: z.string().min(1),
  buildId: z.string().min(1),
  maxTotalChargeUsd: z.number().positive(),
});
export type ActorPin = z.infer<typeof ActorPinSchema>;

const ACTOR_PINS: Record<z.infer<typeof SocialPlatformSchema>, ActorPin> = {
  instagram: {
    actorId: "apify/instagram-post-scraper",
    build: "0.0.605",
    buildId: "GIISxoRALicUa1fhf",
    maxTotalChargeUsd: 0.05,
  },
  tiktok: {
    actorId: "clockworks/tiktok-profile-scraper",
    build: "0.0.480",
    buildId: "lwGl7fhJ6AyHKmT6a",
    maxTotalChargeUsd: 0.05,
  },
  x: {
    actorId: "scrape.badger/twitter-tweets-scraper",
    build: "1.4.18",
    buildId: "121hIm2aXEBh0No54",
    maxTotalChargeUsd: 0.05,
  },
};

export const SocialExtractConfigSchema = z
  .object({
    actors: z.record(SocialPlatformSchema, ActorPinSchema).default(ACTOR_PINS),
    resultsLimit: z.number().int().positive().default(20),
    datasetPageSize: z.number().int().positive().max(100).default(10),
    pollIntervalMs: z.number().int().positive().default(5_000),
    actorTimeoutSeconds: z.number().int().positive().default(600),
    requestTimeoutMs: z.number().int().positive().default(30_000),
    readMaxRetries: z.number().int().min(0).default(4),
    retryDelayMs: z.number().int().positive().default(500),
    assetDownloadRetries: z.number().int().min(0).default(2),
    assetRetryDelayMs: z.number().int().positive().default(500),
    includePinnedPosts: z.boolean().default(true),
    maxAssetsPerPost: z.number().int().positive().default(12),
    maxPostTextChars: z.number().int().positive().default(20_000),
    maxAssetBytes: z.number().int().positive().default(30_000_000),
    maxBundleBytes: z.number().int().positive().default(80_000_000),
    maxUploadBytes: z.number().int().positive().default(85_000_000),
    prefetch: z.number().int().positive().default(2),
    staleAttemptMs: z
      .number()
      .int()
      .positive()
      .default(15 * 60_000),
    dispatchRecoveryMs: z.number().int().positive().default(60_000),
    sweepBatchSize: z.number().int().positive().default(25),
  })
  .strict();
export type SocialExtractConfig = z.infer<typeof SocialExtractConfigSchema>;

export const SocialAnalyseConfigSchema = SandboxTuningConfigSchema.extend({
  agentModel: z.string().default("openai/gpt-6-luna"),
  maxInputTokens: z.number().int().positive().default(250_000),
  maxOutputTokens: z.number().int().positive().default(8_192),
  maxSteps: z.number().int().positive().default(24),
  identicalToolCallLimit: z.number().int().min(2).default(3),
  textOnlyReprompts: z.number().int().min(0).default(1),
  prefetch: z.number().int().positive().default(2),
  cleanupMarginMs: z.number().int().positive().default(30_000),
  minimumLoopMs: z.number().int().positive().default(60_000),
  preflightTimeoutMs: z.number().int().positive().default(15_000),
  sandboxPreflightTimeoutMs: z.number().int().positive().default(180_000),
}).strict();
export type SocialAnalyseConfig = z.infer<typeof SocialAnalyseConfigSchema>;

export const SocialAnalyseAttemptConfigSchema = z
  .object({
    initialBudgetMs: z
      .number()
      .int()
      .positive()
      .default(20 * 60_000),
    maxBudgetMs: z
      .number()
      .int()
      .positive()
      .default(50 * 60_000),
    extendWindowMs: z
      .number()
      .int()
      .positive()
      .default(10 * 60_000),
    maxCaptureAttempts: z.number().int().positive().default(2),
    dispatchRecoveryMs: z.number().int().positive().default(60_000),
    sweepBatchSize: z.number().int().positive().default(25),
    evidenceUrlTtlSeconds: z.number().int().positive().default(3_600),
  })
  .strict();
export type SocialAnalyseAttemptConfig = z.infer<
  typeof SocialAnalyseAttemptConfigSchema
>;

export const SocialReadConfigSchema = z
  .object({
    profileLimit: z.number().int().positive().default(100),
    extractionLimit: z.number().int().positive().default(100),
    captureLimit: z.number().int().positive().default(200),
  })
  .strict();
export type SocialReadConfig = z.infer<typeof SocialReadConfigSchema>;
